import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Eye,
  ToggleLeft,
  ToggleRight,
  Users,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_staff_data,
  delete_staff,
  manage_staff_status,
  APL_LINK,
} from "../serviceconnection/ServiceConnection";

export default function Staff() {
  const navigate = useNavigate();
  const [staffList, setStaffList] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  // ── Fetch ────────────────────────────────────────────────────────────
  const fetchStaff = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      const res = await server_post_data(get_staff_data, fd);
      setStaffList(res.data?.data ?? []);
    } catch {
      showError("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  // ── Filter ───────────────────────────────────────────────────────────
  const filtered = staffList.filter(
    (s) =>
      s.admin_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.admin_email?.toLowerCase().includes(search.toLowerCase()) ||
      s.admin_mobile_no?.includes(search),
  );

  // ── Toggle ───────────────────────────────────────────────────────────
  const handleToggle = async (s) => {
    try {
      const fd = new FormData();
      fd.append("p_id", s.primary_id);
      fd.append("allow_access", s.allow_access === "1" ? "0" : "1");
      const res = await server_post_data(manage_staff_status, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess(res.data?.message);
      setStaffList((prev) =>
        prev.map((item) =>
          item.primary_id === s.primary_id
            ? { ...item, allow_access: s.allow_access === "1" ? "0" : "1" }
            : item,
        ),
      );
    } catch {
      showError("Something went wrong");
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      const fd = new FormData();
      fd.append("p_id", deleteId);
      const res = await server_post_data(delete_staff, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Staff deleted");
      setDeleteId(null);
      setStaffList((prev) => prev.filter((s) => s.primary_id !== deleteId));
    } catch {
      showError("Something went wrong");
    }
  };

  // ── Initials helper ──────────────────────────────────────────────────
  const initials = (name = "") =>
    name
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Staff Management</h1>
          <p className="page-subtitle">
            Manage admin panel users and their access
          </p>
        </div>
        <button
          onClick={() => navigate("/staff/add")}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white"
          style={{ background: "hsl(var(--accent))" }}
        >
          <Plus className="w-4 h-4" /> Add Staff
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          {
            label: "Total Staff",
            value: staffList.length,
            color: "hsl(var(--accent))",
          },
          {
            label: "Active",
            value: staffList.filter((s) => s.allow_access === "1").length,
            color: "hsl(var(--success))",
          },
          {
            label: "Inactive",
            value: staffList.filter((s) => s.allow_access !== "1").length,
            color: "hsl(var(--warning))",
          },
        ].map((s) => (
          <div key={s.label} className="cms-card p-4">
            <p className="text-2xl font-bold" style={{ color: s.color }}>
              {s.value}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="cms-card p-4">
        <div className="search-input max-w-sm">
          <Search className="w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or mobile..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/* Table */}
      <div className="section-panel">
        <div className="overflow-x-auto">
          <table className="w-full cms-table">
            <thead>
              <tr>
                <th>Staff</th>
                <th>Mobile</th>
                <th>Designation</th>
                <th>City</th>
                <th>Access</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-muted-foreground text-sm"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-muted-foreground text-sm"
                  >
                    No staff found
                  </td>
                </tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s.primary_id} className="group">
                    <td>
                      <div className="flex items-center gap-3">
                        {s.admin_image ? (
                          <img
                            src={
                              APL_LINK + (s.admin_image_url || s.admin_image)
                            }
                            alt={s.admin_name}
                            className="w-9 h-9 rounded-full object-cover border border-border flex-shrink-0"
                          />
                        ) : (
                          <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                            style={{ background: "hsl(var(--accent))" }}
                          >
                            {initials(s.admin_name)}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-sm text-foreground">
                            {s.admin_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {s.admin_email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="text-sm text-muted-foreground">
                      {s.admin_mobile_no}
                    </td>
                    <td className="text-sm text-foreground">
                      {s.designation_name ?? "-"}
                    </td>
                    <td className="text-sm text-muted-foreground">
                      {s.admin_city ?? "-"}
                    </td>
                    <td>
                      {s.allow_access === "1" ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                          style={{
                            background: "hsl(var(--success-muted))",
                            color: "hsl(var(--success))",
                          }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />{" "}
                          Active
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                          style={{
                            background: "hsl(var(--destructive-muted))",
                            color: "hsl(var(--destructive))",
                          }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />{" "}
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            navigate(`/staff/view/${s.primary_id}`)
                          }
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggle(s)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                          style={{
                            color:
                              s.allow_access === "1"
                                ? "hsl(var(--success))"
                                : "hsl(var(--warning))",
                          }}
                          title="Toggle Access"
                        >
                          {s.allow_access === "1" ? (
                            <ToggleRight className="w-4 h-4" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() =>
                            navigate(`/staff/edit/${s.primary_id}`)
                          }
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteId(s.primary_id)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                          style={{ color: "hsl(var(--destructive))" }}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {staffList.length} staff members
          </p>
        </div>
      </div>

      {/* Delete Confirm */}
      {deleteId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
          onClick={() => setDeleteId(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-sm mx-4 fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header">
              <h2 className="section-panel-title">Delete Staff</h2>
            </div>
            <div className="section-panel-body space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: "hsl(var(--destructive))" }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
